import { atom, computed } from 'nanostores'

type SubscriptionDuplicateInfo = {
  hasMatches: boolean
  subscription: {
    product_name: string
    customer_center_url: string
  } | null
}

enum DetectionMode {
  Idle,
  CheckingDuplicates,
  FoundDuplicates,
  NoDuplicatesFound,
  Error,
}

export class CheckoutSubscriptionDuplicates {
  static store
  static computed
  static initialized = false

  static initialize(): void {
    if (this.initialized) return

    this.store = {
      mode: atom(DetectionMode.Idle),
      duplicateInfo: atom<SubscriptionDuplicateInfo | null>(null),
      checkedEmails: atom(new Set<string>()),
      lastError: atom<string | null>(null),
    }

    this.computed = {
      showingDuplicates: computed(this.store.mode, (mode) => mode === DetectionMode.FoundDuplicates),
      hasError: computed(this.store.mode, (mode) => mode === DetectionMode.Error),
      duplicateSubscription: computed(this.store.duplicateInfo, (info) => info?.subscription || null),
    }

    this.setupEmailMonitoring()

    this.initialized = true
  }

  static setupEmailMonitoring(): void {
    const validGuestEmail = computed(
      [
        globalThis.Checkout.store.checkout.mode,
        globalThis.Checkout.store.contact,
        globalThis.Checkout.computed.contactErrors,
        globalThis.Checkout.computed.hideContactInformationForm,
      ],
      (mode, { email }, errors, hideContactInformationForm) =>
        mode === 'guest' && !errors?.fields?.email && !hideContactInformationForm && email
    )

    validGuestEmail.subscribe((email) => {
      if (!email) return

      const checkedEmails = this.store.checkedEmails.get()
      if (checkedEmails.has(email)) return

      checkedEmails.add(email)
      this.store.checkedEmails.set(new Set(checkedEmails))

      this.checkForDuplicateSubscriptions(email)
    })
  }

  static async checkForDuplicateSubscriptions(email: string): Promise<void> {
    try {
      this.store.mode.set(DetectionMode.CheckingDuplicates)
      this.store.lastError.set(null)

      const response = await fetch(
        '/user_pages/api/v1/contacts/check_subscription_duplicates.json?' + new URLSearchParams({ email }),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        if (response.status === 404) {
          this.store.duplicateInfo.set({
            hasMatches: false,
            subscription: null,
          })
          this.store.mode.set(DetectionMode.NoDuplicatesFound)
          return
        }

        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (data.result && data.duplicate_subscription) {
        this.store.duplicateInfo.set({
          hasMatches: true,
          subscription: data.duplicate_subscription,
        })
        this.store.mode.set(DetectionMode.FoundDuplicates)
      } else {
        this.store.duplicateInfo.set({
          hasMatches: false,
          subscription: null,
        })
        this.store.mode.set(DetectionMode.NoDuplicatesFound)
      }
    } catch (error) {
      console.error('Subscription duplicate check failed:', error)
      this.store.lastError.set(error.message || 'Unknown error occurred')
      this.store.mode.set(DetectionMode.Error)
    }
  }

  static dismissDuplicates(): void {
    this.store.mode.set(DetectionMode.Idle)
    this.store.duplicateInfo.set(null)
  }

  static reset(): void {
    this.store.mode.set(DetectionMode.Idle)
    this.store.duplicateInfo.set(null)
    this.store.checkedEmails.set(new Set())
    this.store.lastError.set(null)
  }
}
